import React, { useState } from "react";
import { enqueueSnackbar } from "notistack";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../../components/ui/card";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";
import { TextArea } from "../../../../components/TextArea";
import { Badge } from "../../../../components/ui/badge";
import { AlertTriangle, X, Plus, Trash2, Loader2 } from "lucide-react";
import { api } from "../../../../services/api";

interface RelatorioTreinamento {
  id: string;
  treinamento: {
    treinamento: string;
  };
}

interface ErroForm {
  descricao: string;
}

interface RelatarErrosModalProps {
  isOpen: boolean;
  onClose: () => void;
  relatorio: RelatorioTreinamento | null;
  onSuccess: () => void;
}

export function RelatarErrosModal({
  isOpen,
  onClose,
  relatorio,
  onSuccess
}: RelatarErrosModalProps) {
  const [loading, setLoading] = useState(false);
  const [erros, setErros] = useState<ErroForm[]>([
    { descricao: "" }
  ]);

  const adicionarErro = () => {
    setErros([...erros, { descricao: "" }]);
  };

  const removerErro = (index: number) => {
    if (erros.length > 1) {
      setErros(erros.filter((_, i) => i !== index));
    }
  };

  const atualizarErro = (index: number, campo: keyof ErroForm, valor: string) => {
    const novosErros = [...erros];
    novosErros[index] = { ...novosErros[index], [campo]: valor };
    setErros(novosErros);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!relatorio) return;

    // Validar se todos os erros têm descrição
    const errosValidos = erros.filter(erro => erro.descricao.trim());
    if (errosValidos.length === 0) {
      enqueueSnackbar("Adicione pelo menos um erro com descrição", { variant: "warning" });
      return;
    }

    try {
      setLoading(true);

      await api.post("/treinamentos/relatorios/erros", {
        id_treinamento_relatorio: relatorio.id,
        erros: errosValidos
      });

      enqueueSnackbar(`${errosValidos.length} erro(s) relatado(s) com sucesso!`, { variant: "success" });
      onSuccess();
      onClose();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao relatar erros", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setErros([{ descricao: "" }]);
    onClose();
  };



  if (!isOpen || !relatorio) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-600" />
              Relatar Erros
            </CardTitle>
            <CardDescription>
              {relatorio.treinamento.treinamento} - {relatorio.id}
            </CardDescription>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            className="h-6 w-6 p-0"
            disabled={loading}
          >
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg border border-orange-200 dark:border-orange-700">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                <h4 className="font-medium text-orange-800 dark:text-orange-300">Sobre Relatório de Erros</h4>
              </div>
              <p className="text-sm text-orange-700 dark:text-orange-400">
                Use este formulário para reportar problemas identificados no relatório de treinamento.
                Você pode adicionar múltiplos erros para correção.
              </p>
            </div>

            {erros.map((erro, index) => (
              <div key={index} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <h5 className="font-medium text-gray-900 dark:text-gray-100">Erro #{index + 1}</h5>
                  <div className="flex items-center gap-2">
                    {erros.length > 1 && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => removerErro(index)}
                        className="text-red-600 border-red-200 hover:bg-red-50 dark:text-red-400 dark:border-red-700 dark:hover:bg-red-900/30"
                        disabled={loading}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>

                <div>
                  <Label htmlFor={`descricao-${index}`}>Descrição do Erro</Label>
                  <TextArea
                    id={`descricao-${index}`}
                    title=""
                    value={erro.descricao}
                    onChange={(e) => atualizarErro(index, "descricao", e.target.value)}
                    placeholder="Descreva detalhadamente o erro encontrado..."
                    className="mt-1"
                    disabled={loading}
                    rows={3}
                  />
                </div>


              </div>
            ))}

            <div className="flex justify-center">
              <Button
                type="button"
                variant="outline"
                onClick={adicionarErro}
                className="border-[oklch(0.45_0.15_142)] text-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.45_0.15_142)] hover:text-white"
                disabled={loading}
              >
                <Plus className="h-4 w-4 mr-2" />
                Adicionar Outro Erro
              </Button>
            </div>

            <div className="flex gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                className="flex-1"
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={loading || erros.every(erro => !erro.descricao.trim())}
                className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Relatando...
                  </>
                ) : (
                  <>
                    <AlertTriangle className="w-4 h-4 mr-2" />
                    Relatar {erros.filter(e => e.descricao.trim()).length} Erro(s)
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
